import { UserRoleEnum } from '@/types/auth.types';

const ROUTES_PATH = {
  root: '/',
  login: '/login',
  changePassword: '/change-password',
  shopList: '/shop-management',
  shopDetail: '/shop-management/:id',
  shopWeddingsDetail: '/shop-management/weddings/:id',
  myOrganization: '/my-organization',
  myOrganizationAccountsNew: '/my-organization/accounts/new',
  myOrganizationAccountsDetail: '/my-organization/accounts/:accountId',
  myOrganizationShopsNew: '/my-organization/shops/new',
  myOrganizationShopsWeddingsNew: '/my-organization/shops/weddings/new',
  myOrganizationShopsDetail: '/my-organization/shops/:shopId',
  myOrganizationShopsWeddingsDetail: '/my-organization/shops/weddings/:shopId',
  myOrganizationDrinkMaterialsNew: '/my-organization/drink-materials/new',
  myOrganizationDrinkMaterialsDetail:
    '/my-organization/drink-materials/:materialId',
  organizationList: '/organization-management',
  organizationNew: '/organization-management/new',
  organizationDetail: '/organization-management/:id',
  organizationAccountsNew: '/organization-management/:id/accounts/new',
  organizationAccountsDetail:
    '/organization-management/:id/accounts/:accountId',
  organizationShopsNew: '/organization-management/:id/shops/new',
  organizationShopsWeddingsNew:
    '/organization-management/:id/shops/weddings/new',
  organizationShopsDetail: '/organization-management/:id/shops/:shopId',
  organizationShopsWeddingDetail:
    '/organization-management/:id/shops/weddings/:shopId',
  organizationDrinkMaterialsNew:
    '/organization-management/:id/drink-materials/new',
  organizationDrinkMaterialsDetail:
    '/organization-management/:id/drink-materials/:materialId',
  organizationPrinterAndFixturesNew:
    '/organization-management/:id/printer-and-fixtures/new',
  organizationPrinterAndFixturesDetail:
    '/organization-management/:id/printer-and-fixtures/:fixtureId',
  organizationAcodeUpdate: '/organization-management/acode-update',
  passwordReset: '/password-reset/new-password',
  passwordResetSuccess: '/password-reset/success',
  passwordResetError: '/password-reset/error',
  passwordResetExpiredError: '/password-reset/expired-error',
  passwordResetSendEmail: '/password-reset/send-email',
  passwordResetSendEmailSuccess: '/password-reset/send-email-success',
  passwordRegistration: '/password-registration',
  passwordRegistrationSuccess: '/password-registration/success',
  passwordRegistrationError: '/password-registration/error',
  passwordRegistrationExpiredError: '/password-registration/expired-error',
  passwordRegistrationEmailError: '/password-registration/email-error',
  accountList: '/account-management',
  myAccount: '/my-account',
  shopApprovalRequestsList: '/shop-approval-requests-management',
  shopApprovalRequestsDetail: '/shop-approval-requests-management/:id',
  salesResultsApplication: '/sales-results-application',
  salesResultsApplicationUpdate: '/sales-results-application/:id',
  salesResultsApplicationAmountAdjustmentDetail:
    '/sales-results-application/:id/amount-adjustment-details',
  salesResultsApplicationStatusAmountAdjustment:
    '/sales-results-application-status/:id/amount-adjustment',
  salesResultsApplicationStatusAmountAdjustmentDetail:
    '/sales-results-application-status/:id/amount-adjustment-details',
  salesResultsApplicationStatusList: '/sales-results-application-status',
  salesResultsApplicationStatusDetail: '/sales-results-application-status/:id',
  salesPerformanceHistoryList: '/sales-performance-history-management',
  drinkManagement: '/drink-management',
  drinkDetail: '/drink-management/:id',
  drinkNew: '/drink-management/new',
  shopDrinkList: '/shop-drink-management',
  shopDrinkDetail: '/shop-drink-management/:id',
  bannerManagement: '/banner-management',
  bannerNew: '/banner-management/new',
  bannerDetail: '/banner-management/:id',
  usageGuide: '/usage-guide',
  faq: '/faq',
  ipLabelList: '/label-management',
  ipLabelNew: '/label-management/new',
  ipLabelDetail: '/label-management/:id',
  labelOrderList: '/label-order-management',
  snapLabelList: '/snap-label-management',
  snapLabelDetail: '/snap-label-management/:id',
  fullIpLabelList: '/full-custom-label-management',
  fullIpLabelNew: '/full-custom-label-management/new',
  fullIpLabelDetail: '/full-custom-label-management/:id',
} as const;

const AUTH_BYPASS_CHECK_ROUTES = [
  ROUTES_PATH.passwordRegistration,
  ROUTES_PATH.passwordRegistrationSuccess,
  ROUTES_PATH.passwordRegistrationError,
  ROUTES_PATH.passwordRegistrationExpiredError,
  ROUTES_PATH.passwordRegistrationEmailError,
  ROUTES_PATH.passwordReset,
  ROUTES_PATH.passwordResetSuccess,
  ROUTES_PATH.passwordResetError,
  ROUTES_PATH.passwordResetExpiredError,
  ROUTES_PATH.passwordResetSendEmail,
  ROUTES_PATH.passwordResetSendEmailSuccess,
] as const;

const FEATURE_PUBLIC_ROUTES = [
  ROUTES_PATH.root,
  ROUTES_PATH.changePassword,
  ROUTES_PATH.myAccount,
  ROUTES_PATH.usageGuide,
  ROUTES_PATH.faq,
  ROUTES_PATH.bannerManagement,
  ROUTES_PATH.bannerNew,
  ROUTES_PATH.bannerDetail,
  ROUTES_PATH.ipLabelList,
  ROUTES_PATH.ipLabelNew,
  ROUTES_PATH.ipLabelDetail,
  ROUTES_PATH.fullIpLabelList,
  ROUTES_PATH.fullIpLabelNew,
  ROUTES_PATH.fullIpLabelDetail,
] as const;

const FEATURE_SBS_ROUTES = [
  ROUTES_PATH.organizationList,
  ROUTES_PATH.organizationDetail,
  ROUTES_PATH.organizationAccountsNew,
  ROUTES_PATH.organizationAccountsDetail,
  ROUTES_PATH.organizationDrinkMaterialsNew,
  ROUTES_PATH.organizationDrinkMaterialsDetail,
  ROUTES_PATH.organizationPrinterAndFixturesNew,
  ROUTES_PATH.organizationPrinterAndFixturesDetail,
  ROUTES_PATH.organizationShopsDetail,
  ROUTES_PATH.organizationShopsWeddingDetail,
  ROUTES_PATH.accountList,
  ROUTES_PATH.shopApprovalRequestsList,
  ROUTES_PATH.shopApprovalRequestsDetail,
  ROUTES_PATH.shopList,
  ROUTES_PATH.shopDetail,
  ROUTES_PATH.shopWeddingsDetail,
  ROUTES_PATH.salesPerformanceHistoryList,
  ROUTES_PATH.salesResultsApplicationStatusList,
  ROUTES_PATH.salesResultsApplicationStatusDetail,
  ROUTES_PATH.snapLabelList,
  ROUTES_PATH.snapLabelDetail,
  ROUTES_PATH.labelOrderList,
] as const;

const FEATURE_ORGANIZATION_ROUTES = [
  ROUTES_PATH.myOrganization,
  ROUTES_PATH.myOrganizationAccountsNew,
  ROUTES_PATH.myOrganizationAccountsDetail,
  ROUTES_PATH.myOrganizationDrinkMaterialsNew,
  ROUTES_PATH.myOrganizationDrinkMaterialsDetail,
  ROUTES_PATH.myOrganizationShopsNew,
  ROUTES_PATH.myOrganizationShopsWeddingsNew,
  ROUTES_PATH.myOrganizationShopsDetail,
  ROUTES_PATH.myOrganizationShopsWeddingsDetail,
  ROUTES_PATH.salesResultsApplication,
  ROUTES_PATH.salesResultsApplicationUpdate,
  ROUTES_PATH.salesPerformanceHistoryList,
  ROUTES_PATH.salesResultsApplicationAmountAdjustmentDetail,
  ROUTES_PATH.shopDrinkList,
  ROUTES_PATH.shopDrinkDetail,
  ROUTES_PATH.labelOrderList,
  ROUTES_PATH.snapLabelList,
  ROUTES_PATH.snapLabelDetail,
] as const;

const FEATURE_MASTER_ROUTES = [
  ROUTES_PATH.organizationShopsNew,
  ROUTES_PATH.organizationShopsWeddingsNew,
  ROUTES_PATH.salesResultsApplicationStatusAmountAdjustment,
  ROUTES_PATH.salesResultsApplicationStatusAmountAdjustmentDetail,
  ROUTES_PATH.drinkManagement,
  ROUTES_PATH.drinkDetail,
  ROUTES_PATH.drinkNew,
];

const FEATURE_PRIVATE_ROUTES = {
  [UserRoleEnum.sbf_master]: [
    ROUTES_PATH.organizationAcodeUpdate,
    ...FEATURE_PUBLIC_ROUTES,
    ...FEATURE_SBS_ROUTES,
    ...FEATURE_MASTER_ROUTES,
  ],
  [UserRoleEnum.sbs_operation]: [
    ...FEATURE_PUBLIC_ROUTES,
    ...FEATURE_SBS_ROUTES,
    ...FEATURE_MASTER_ROUTES,
  ],
  [UserRoleEnum.sbs_sales]: [...FEATURE_PUBLIC_ROUTES, ...FEATURE_SBS_ROUTES],
  [UserRoleEnum.sbs_tag_team]: [
    ...FEATURE_PUBLIC_ROUTES,
    ...FEATURE_SBS_ROUTES,
  ],
  [UserRoleEnum.organization]: [
    ...FEATURE_PUBLIC_ROUTES,
    ...FEATURE_ORGANIZATION_ROUTES,
  ],
  [UserRoleEnum.wedding]: [...FEATURE_PUBLIC_ROUTES],
} as Record<UserRoleEnum, string[]>;

export { AUTH_BYPASS_CHECK_ROUTES, FEATURE_PRIVATE_ROUTES, ROUTES_PATH };
